import React from 'react';
import Skeleton from '../Skeleton/index';
import './table-skeleton.scss';

interface TableSkeletonProps {
  /** Number of rows to display */
  rows?: number;
  /** Number of columns to display */
  columns?: number;
  /** Show table header skeleton */
  showHeader?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Column widths - if provided, should match number of columns */
  columnWidths?: string[];
  /** Height for each row */
  rowHeight?: string;
  /** 36px header and rows, matching the ListPage shell's dense table. */
  compact?: boolean;
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 8,
  columns = 6,
  showHeader = true,
  className = '',
  columnWidths = [],
  rowHeight,
  compact = false
}) => {
  const resolvedRowHeight = rowHeight ?? (compact ? '36px' : '56px');
  // Generate default column widths if not provided
  const getColumnWidth = (index: number): string => {
    if (columnWidths[index]) {
      return columnWidths[index];
    }
    
    // Default widths that work well for most tables
    const defaultWidths = ['12%', '25%', '15%', '15%', '20%', '13%'];
    return defaultWidths[index] || '15%';
  };

  return (
    <div className={`table-skeleton ${compact ? 'table-skeleton--compact' : ''} ${className}`.trim()}>
      <div className="table-skeleton-wrapper">
        <table className="table-skeleton-table">
          {/* Header skeleton */}
          {showHeader && (
            <thead>
              <tr className="table-skeleton-header-row">
                {Array.from({ length: columns }).map((_, index) => (
                  <th 
                    key={`header-${index}`}
                    className="table-skeleton-header-cell"
                    style={{ width: getColumnWidth(index) }}
                  >
                    <Skeleton 
                      height={compact ? '10px' : '20px'} 
                      width="80%"
                      animation="wave"
                    />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          
          {/* Body skeleton */}
          <tbody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr 
                key={`row-${rowIndex}`}
                className="table-skeleton-body-row"
                style={{ height: resolvedRowHeight }}
              >
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <td 
                    key={`cell-${rowIndex}-${colIndex}`}
                    className="table-skeleton-body-cell"
                    style={{ width: getColumnWidth(colIndex) }}
                  >
                    <Skeleton 
                      height={compact ? '12px' : '16px'} 
                      width={colIndex === 0 ? '60%' : '90%'} // First column usually shorter (IDs, etc.)
                      animation="wave"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableSkeleton;
